import { createContext, useContext, useState, useEffect, useCallback, ReactNode } from "react";
import { useAuth } from "./useAuth";

interface UnreadContactCountContextType {
  unreadContactCount: number;
  decrementUnreadCount: () => void;
  refreshUnreadCount: () => void;
}

const UnreadContactCountContext = createContext<UnreadContactCountContextType>({
  unreadContactCount: 0,
  decrementUnreadCount: () => {},
  refreshUnreadCount: () => {},
});

export function UnreadContactCountProvider({ children }: { children: ReactNode }) {
  const { user } = useAuth();
  const [unreadContactCount, setUnreadContactCount] = useState(0);

  const fetchUnreadCount = useCallback(() => {
    if (!user || user.role !== "admin") return;
    fetch("/api/admin/contact-submissions/unread-count", { credentials: "include" })
      .then(res => res.ok ? res.json() : null)
      .then(data => { if (data) setUnreadContactCount(data.count); })
      .catch(() => {});
  }, [user]);

  useEffect(() => {
    if (!user || user.role !== "admin") {
      setUnreadContactCount(0);
      return;
    }
    fetchUnreadCount();
    const interval = setInterval(fetchUnreadCount, 60000);
    return () => clearInterval(interval);
  }, [user, fetchUnreadCount]);

  const decrementUnreadCount = useCallback(() => {
    setUnreadContactCount(prev => Math.max(0, prev - 1));
  }, []);

  const refreshUnreadCount = useCallback(() => {
    fetchUnreadCount();
  }, [fetchUnreadCount]);

  return (
    <UnreadContactCountContext.Provider value={{ unreadContactCount, decrementUnreadCount, refreshUnreadCount }}>
      {children}
    </UnreadContactCountContext.Provider>
  );
}

export function useUnreadContactCount() {
  return useContext(UnreadContactCountContext);
}
